import { useCallback, useState } from 'react';
import { IPackage, IPostTariff } from '../../../../interfaces';

export interface IStepperFormData {
    id?: string;
    name: string;
    description: string;
    companyId?: string;
    companyIds: number[];
    initialTariffLocalPackagesIds?: number[];
    initialTariffRoamingPackagesIds?: number[];
    outOfBundle: boolean;
    planType?: string;
    price?: number;
    priceCents?: number;
    productId?: string;
    tariffPackageIds: number[];
    tariffLocalPackagesIds?: number[];
    tariffRoamingPackagesIds?: number[];
    tariffPackages?: IPackage[];
}

export interface IStepperState {
    formData: IStepperFormData;
    isSubmitting: boolean;
    updateFormData: (updates: Partial<IStepperFormData>) => void;
    resetFormData: () => void;
    initializeFormData: (initialData: Partial<IStepperFormData>) => void;
    getPostTariffData: () => IPostTariff;
    setSubmitting: (submitting: boolean) => void;
}

const defaultFormData: IStepperFormData = {
    name: '',
    price: 0,
    priceCents: 0,
    outOfBundle: false,
    tariffPackageIds: [],
    tariffLocalPackagesIds: [],
    tariffRoamingPackagesIds: [],
    companyIds: [],
    description: '',
    companyId: '',
};

export const useStepperState = (initialData?: Partial<IStepperFormData>): IStepperState => {
    const [formData, setFormData] = useState<IStepperFormData>({
        ...defaultFormData,
        ...initialData,
    });
    const [isSubmitting, setIsSubmitting] = useState(false);

    const updateFormData = useCallback((updates: Partial<IStepperFormData>) => {
        setFormData((prev) => ({
            ...prev,
            ...updates,
        }));
    }, []);

    const resetFormData = useCallback(() => {
        setFormData(defaultFormData);
    }, []);

    const initializeFormData = useCallback((initialData: Partial<IStepperFormData>) => {
        setFormData({
            ...defaultFormData,
            ...initialData,
        });
    }, []);

    const getPostTariffData = useCallback((): IPostTariff => {
        return {
            name: formData.name,
            companyId: formData.companyId || '',
            companyIds: formData.companyIds,
            description: formData.description,
            initialTariffLocalPackagesIds: formData.initialTariffLocalPackagesIds || [],
            initialTariffRoamingPackagesIds: formData.initialTariffRoamingPackagesIds || [],
            outOfBundle: formData.outOfBundle,
            planType: formData.planType,
            priceCents: formData.price ? formData.price * 100 : 0,
            productId: formData.productId,
            tariffPackageIds: formData.tariffPackageIds,
            tariffLocalPackagesIds: formData.tariffLocalPackagesIds || [],
            tariffRoamingPackagesIds: formData.tariffRoamingPackagesIds || [],
            tariffPackages: formData.tariffPackages,
        };
    }, [formData]);

    const setSubmitting = useCallback((submitting: boolean) => {
        setIsSubmitting(submitting);
    }, []);

    return {
        formData,
        isSubmitting,
        updateFormData,
        resetFormData,
        initializeFormData,
        getPostTariffData,
        setSubmitting,
    };
};
